/**
 * Generates the personalised "What's New since your last visit" briefing by
 * asking the Cockpit AI agent.
 *
 * The agent has data tools, so this does not pre-fetch KPIs. It supplies the
 * *context* the agent can't know — who the user is, what they look after, and
 * what they personally opened and when — and lets the agent pull the numbers.
 *
 * Role handling: the Keycloak role is free text ("CEO Focke", "production leader
 * factory XY"), so there is no vocabulary to map it onto. It is passed through
 * verbatim and the agent decides what level of detail suits it. A hard-coded tier
 * table would go stale the moment someone types a new job title into Keycloak.
 *
 * ## Prompt shape is load-bearing
 *
 * The agent service runs its own prompt filter and answers a rejected request
 * with a canned sentence delivered as a normal `text_delta` (HTTP 200, no error
 * event). Earlier revisions of this prompt tripped it. So the prompt is kept
 * deliberately short, in the first person, and free of the patterns such filters
 * look for: no persona assignment, no "respond exactly and nothing else", no
 * instructions to withhold reasoning, and no `###MARKER###` delimiters — the
 * hash-delimited section format is common in prompt-injection payloads.
 *
 * Because there are no markers, the machines the briefing mentions are recovered
 * by scanning the reply for ids from the user's own park rather than asking the
 * model for a second structured section. Fewer instructions, less to reject, and
 * nothing to mis-format.
 */

import { createSession, deleteSession, streamChat } from "./CockpitService";
import { isWhatsNewDebugEnabled } from "../utils/debugFlags";
import type {
  InsightMachineRef,
  StoredInsightSummary,
} from "./UserInsightStore";

/** A machine in the user's park. */
export interface WhatsNewMachine {
  /** Serial number / business key used for deep links and Doris lookups. */
  id: string;
  name?: string;
  model?: string;
  line?: string;
  factory?: string;
}

/** Something the user personally opened, with when they last did so. */
export interface WhatsNewVisit {
  type: "machine" | "line";
  id: string;
  name?: string;
  /** Epoch ms of the most recent view. */
  lastViewedAt: number;
}

export interface WhatsNewInput {
  /** Free-text Keycloak role, or null for a generalised briefing. */
  role: string | null;
  /** Start of the period to report on — the previous briefing. */
  windowStart: Date;
  now: Date;
  /** Newest-first, from the cross-browser navigation history. */
  visited: WhatsNewVisit[];
  /**
   * The machines this user has access to.
   *
   * Used only locally, to turn ids mentioned in the reply into links — it is
   * deliberately NOT sent in the prompt. The agent holds the user's JWT and its
   * data tools are org-scoped, so it can enumerate the park itself; listing
   * hundreds of serial numbers would only bloat the request.
   */
  park: WhatsNewMachine[];
  /** UI language code, so the briefing matches the app. */
  language?: string;
}

/** Prompt size guard for the visited list. */
const MAX_VISITS_IN_PROMPT = 12;

/** Machine links rendered alongside the card. */
const MAX_MACHINE_REFS = 8;

/** The agent may call several tools, so allow well beyond a normal chat turn. */
const GENERATION_TIMEOUT_MS = 180_000;

/**
 * Log the prompt and reply.
 *
 * Gated (see `isWhatsNewDebugEnabled`) because the prompt embeds the user's
 * machine serial numbers and names — internal data rather than secrets, but it
 * has no business sitting in every user's console in production.
 */
function logBlock(label: string, body: string): void {
  if (!isWhatsNewDebugEnabled()) return;
  // Collapsed: the prompt is long, and this keeps it out of the way until wanted.
  console.groupCollapsed(`🧠 ${label} (${body.length} chars)`);
  console.log(body);
  console.groupEnd();
}

function formatDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function describeAge(from: number, now: number): string {
  const days = Math.floor((now - from) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

/** Language names, so the prompt reads "in German" rather than "in de". */
const LANGUAGE_NAMES: Record<string, string> = {
  en: "English",
  de: "German",
  fr: "French",
};

function languageName(code: string | undefined): string {
  if (!code) return "English";
  return LANGUAGE_NAMES[code.slice(0, 2).toLowerCase()] ?? "English";
}

/**
 * One readable line per visited item.
 *
 * The stored display name usually already contains the id — e.g. id `21304` with
 * name `"21304 - Modell HIP (21304)"` — so printing both produced
 * "21304 (21304 - Modell HIP (21304))". This keeps the id once and only appends a
 * name that adds something.
 */
function describeVisit(visit: WhatsNewVisit, now: number): string {
  const kind = visit.type === "line" ? "line" : "machine";
  const raw = (visit.name ?? "").trim();

  // Strip repeats of the id and the "Line <id>" placeholder the store falls back
  // to, then collapse the leftover separators.
  const cleaned = raw
    .split(new RegExp(`\\(?${visit.id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\)?`, "g"))
    .join(" ")
    .replace(/^\s*[-–—]\s*/, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  const label = cleaned && cleaned.toLowerCase() !== "line" ? ` — ${cleaned}` : "";

  return `- ${kind} ${visit.id}${label}, last opened ${formatDay(
    new Date(visit.lastViewedAt),
  )} (${describeAge(visit.lastViewedAt, now)})`;
}

/**
 * Build the request sent to the agent.
 *
 * Kept short and first-person on purpose — see the note at the top of this file
 * about the service's prompt filter.
 */
export function buildPrompt(input: WhatsNewInput): string {
  // `park` is intentionally not destructured: it's for local link extraction, not
  // for the prompt. See WhatsNewInput.park.
  const { role, windowStart, now, visited, language } = input;

  const windowDays = Math.max(
    1,
    Math.round((now.getTime() - windowStart.getTime()) / 86_400_000),
  );

  const visitLines = visited
    .slice(0, MAX_VISITS_IN_PROMPT)
    .map((v) => describeVisit(v, now.getTime()))
    .join("\n");

  // The role is free text and the agent is the one that should interpret it. We
  // deliberately don't hand it a checklist of metrics: what matters to a plant
  // director is not what matters to a line operator, and enumerating measures
  // here would flatten that distinction back out.
  const roleLine = role
    ? `I work as ${role}. Please think about what someone in that role actually needs to know about how the machines are running, decide for yourself which figures and comparisons speak to that, and build the briefing around them.`
    : `No particular role is recorded for me, so please use your own judgement about what would be most worth knowing, and build the briefing around that.`;

  return [
    `I've been away since ${formatDay(windowStart)} (about ${windowDays} days) and would like to catch up on how my packaging machines have been running in that time.`,
    `${roleLine} Please answer in ${languageName(language)}.`,
    `Machines and lines I've opened before, most recent first — useful if you want to say how something has moved since I last looked at it:`,
    visitLines || "- nothing recorded",
  ].join("\n\n");
}

/**
 * House directives for the briefing, sent as `instructions` rather than as part
 * of the message.
 *
 * This is why the request stopped being rejected: the guardrail scans `message`,
 * and a directive block this size sitting there reads as a prompt attack.
 * `instructions` is folded into the system prompt instead and skips that scan.
 *
 * Because it skips the scan it is a module-level constant and must stay one. No
 * user input, no Keycloak values, nothing from the machine data — those all
 * belong in the message, where they are scanned. Interpolating anything here
 * would build a clean path around PROMPT_ATTACK.
 */
const DASHBOARD_INSTRUCTIONS = [
  `You are building a compact visual dashboard, not writing a report. The output is rendered directly into a panel in the application. Earlier attempts came back as paragraphs of prose in coloured boxes — that is the thing to avoid.`,
  `The context is the Performance Cockpit, an OEE and production monitoring application for FOCKE packaging machines. The user wants to see what has changed since they were last here.`,
  `Use your data tools to find out what actually happened over the period the user gives, pulling whichever measures and breakdowns suit the angle you judge relevant for them. Base everything on figures you retrieved, and leave out anything you couldn't retrieve.`,
  `Cover only what genuinely matters: notable movements, exceptions, anything worth acting on or asking about. This is not a changelog — do not walk through every machine, and do not include changes that are just normal variation. A handful of real findings beats a complete inventory; if only one thing stands out, one finding is the right answer, and if nothing does, say so plainly.`,
  `Every item must be carried by a visual, not by a sentence. Each one needs a figure and a graphic — a kpi tile, a bar, a ring gauge, a sparkline or a badge — and at most one short caption of roughly a dozen words beside it. Never write a paragraph: if you feel one coming, the number belongs in a tile and the words belong in its caption.`,
  `Include at least one chart drawn as inline SVG, and make it a full-width feature rather than a thumbnail — it should be one of the first things the eye lands on. Wherever you have a value across the period, or several machines to compare, draw it rather than describing its shape in words.`,
  `Put a chart's heading in ordinary HTML above the svg, not inside it as an svg text element — svg text scales with the drawing and comes out oversized. Inside an svg, keep text to axis labels and data values only, at font-size 12 or smaller, and never larger than the surrounding page text.`,
  `Only use an svg when you are actually drawing something — lines, bars, areas, points. Never use one as a container for words: a caption, a fault name or a duration is ordinary HTML text. An svg holding nothing but text elements renders as a large empty box with the words stranded inside it.`,
  `A good shape is: a grid of kpi tiles for the headline numbers, then each exception as a compact card or ranked row carrying its own bar or gauge, then a chart for any trend worth seeing. The whole thing should be readable at a glance, in a few seconds, with no block of text anywhere in it.`,
  `Everything must be self-contained — the render frame has no network access, so no external images, fonts, scripts or stylesheets.`,
  `The host stylesheet already provides the following, so little or no custom CSS should be needed:
- layout: classes card, tile, and grid for auto-fitting columns; section with a section-title child
- numbers: a kpi container holding kpi-label, kpi-value and kpi-delta children, plus up, down or flat for direction colour
- bars: a bar container holding a bar-fill child whose width is set inline as a percentage; add good, warn or bad to the fill for colour
- ring gauges: a gauge element with the custom property pct set inline to a number from 0 to 100, with the label as a child span; add good, warn or bad for colour
- rankings: a row container holding row-label and row-value children
- status: badge together with badge-success, badge-warning or badge-error, and muted for secondary text
- charts: an svg element with class chart for a main chart, or class spark for a small inline trend. Use a viewBox of 0 0 900 260 for a main chart and 0 0 900 90 for a sparkline, and set no width or height attributes. Those dimensions matter: the svg is stretched to roughly 900 pixels wide, so one viewBox unit is about one pixel and anything you draw comes out the intended size. A smaller viewBox gets magnified and any text in it becomes enormous. Strokes, fills and text are themed automatically, and class area on a path shades the region under a line`,
  `Use whichever of those fit what you chose to show, and group the output rather than running it together as one long list — but only as much structure as there are findings to fill.`,
  `The host already renders the surrounding chrome, so leave these out to avoid showing them twice: no overall title such as "Performance Briefing", no date-range or "since your last visit" subheading, no list of the machines the user recently opened, and no links. Start straight in with the findings.`,
  `Return only the markup — no explanation before or after it, and no commentary about what you queried or how.`,
].join("\n\n");

/** Phrases the service returns when it refuses, so we never cache one. */
const REFUSAL_MARKERS = [
  "blocked by security filters",
  "blocked by content filters",
];

function looksLikeRefusal(text: string): boolean {
  const lower = text.toLowerCase();
  return REFUSAL_MARKERS.some((marker) => lower.includes(marker));
}

/** Strip a leading ```html / ``` fence that models add unprompted. */
function stripCodeFence(text: string): string {
  const trimmed = text.trim();
  const fence = trimmed.match(/^```[a-zA-Z]*\s*\n([\s\S]*?)\n?```$/);
  return fence ? fence[1].trim() : trimmed;
}

/**
 * Block-level tags a briefing plausibly opens with.
 *
 * Deliberately not `span`/`b`/`a`: those appear inside narration often enough
 * ("the <b>21304</b> machine…") that anchoring on them would keep the prose.
 */
const MARKUP_START =
  /<(?:div|section|article|main|header|table|figure|svg|h[1-6]|ul|ol|p)\b[^>]*>/i;

/**
 * A fenced block anywhere in the reply whose contents are markup.
 *
 * Models often wrap the answer in ```html … ``` after narrating first. Taking the
 * fence contents is more precise than tag-scanning, because the fence tells us
 * exactly where the answer starts and stops.
 */
function findFencedMarkup(text: string): string | null {
  const fence = /```(?:html|xml)?[ \t]*\r?\n?([\s\S]*?)```/gi;
  let match: RegExpExecArray | null;
  while ((match = fence.exec(text)) !== null) {
    const body = (match[1] ?? "").trim();
    if (body && MARKUP_START.test(body)) return body;
  }
  return null;
}

/**
 * Index just past the last tag in the string, so trailing prose can be cut.
 *
 * Considers both closing tags and self-closing ones, since a dashboard can end on
 * either.
 */
function lastMarkupEnd(text: string): number {
  let end = -1;
  const patterns = [/<\/[a-zA-Z][^>]*>/g, /<[a-zA-Z][^>]*\/>/g];
  for (const pattern of patterns) {
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
      end = Math.max(end, match.index + match[0].length);
    }
  }
  return end;
}

/**
 * Keep only the markup, discarding everything the agent said around it.
 *
 * A tool-using agent thinks out loud between calls — "Let me fix the query…",
 * "Now let me check the line…" — and each of those arrives as a `text_delta`, so
 * the accumulated reply is paragraphs of working-out wrapped around the actual
 * answer. It also tends to sign off afterwards ("Let me know if you'd like more
 * detail"). None of that should render.
 *
 * Asking the model not to narrate helps but can't be relied on, so this trims it
 * mechanically: prefer a fenced block if there is one, otherwise cut from the
 * first block-level tag to the end of the last tag. Returns the input unchanged
 * when there's no markup at all, which the caller rejects.
 */
export function extractMarkup(text: string): string {
  const source = findFencedMarkup(text) ?? text;

  const start = source.match(MARKUP_START);
  if (!start || start.index === undefined) return source.trim();

  const end = lastMarkupEnd(source);
  return (
    end > start.index ? source.slice(start.index, end) : source.slice(start.index)
  ).trim();
}

/**
 * Whether the reply actually contains markup.
 *
 * This is the guard against caching a refusal. The agent service answers a
 * blocked request with a plain sentence over a normal `text_delta` and HTTP 200,
 * so without this check the refusal would be stored as the day's briefing and
 * shown for the next 24 hours.
 */
export function looksLikeMarkup(text: string): boolean {
  return /<[a-zA-Z][^>]*>/.test(text);
}

/**
 * Recover the machines the briefing mentions by matching ids from the user's own
 * park against the reply.
 *
 * Derived rather than requested: it keeps the prompt to a single instruction, and
 * it can't produce a dead link because an id has to be in the park to match.
 */
export function extractMachineRefs(
  html: string,
  park: WhatsNewMachine[],
): InsightMachineRef[] {
  const refs: InsightMachineRef[] = [];

  // Longest ids first, so "21309" can't shadow a longer id containing it.
  const candidates = [...park].sort((a, b) => b.id.length - a.id.length);

  for (const machine of candidates) {
    if (refs.length >= MAX_MACHINE_REFS) break;
    const id = machine.id.trim();
    if (!id) continue;

    const escaped = id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Word boundaries so an id doesn't match inside a longer number.
    if (!new RegExp(`(?<![\\w-])${escaped}(?![\\w-])`).test(html)) continue;

    refs.push({ id, label: machine.name || machine.model || undefined });
  }

  return refs;
}

/**
 * Ask the agent for the briefing.
 *
 * @throws if the session can't be created, the stream fails, or the reply is not
 *         usable markup (which is how a filtered request arrives). Callers should
 *         treat failure as "keep the previous briefing and try again", never as
 *         fatal — and must not cache the result.
 */
export async function generateWhatsNewSummary(
  token: string,
  input: WhatsNewInput,
): Promise<StoredInsightSummary> {
  const session = await createSession(
    token,
    `[What's New] ${formatDay(input.now)}`,
  );

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), GENERATION_TIMEOUT_MS);

  const prompt = buildPrompt(input);
  logBlock("What's New message", prompt);
  logBlock("What's New instructions", DASHBOARD_INSTRUCTIONS);

  try {
    let text = "";
    let finalResponse: string | undefined;

    for await (const event of streamChat(
      token,
      session.id,
      prompt,
      controller.signal,
      DASHBOARD_INSTRUCTIONS,
    )) {
      switch (event.type) {
        case "text_delta":
          text += event.text ?? "";
          break;
        case "message_stop":
          // Some backends send the complete text here; prefer it when present
          // since it can't be missing a dropped delta.
          if (event.response) finalResponse = event.response;
          break;
        case "error":
          // Tagged distinctly from the HTTP-level failure thrown by streamChat
          // ("Cockpit chat error <status>"), so the two are told apart in logs.
          throw new Error(`Agent stream error: ${event.error || "unspecified"}`);
        default:
          // reasoning_delta / tool_call / tool_result / block_* / metadata are
          // progress signals and contribute nothing to the rendered briefing.
          break;
      }
    }

    const reply = (finalResponse ?? text).trim();
    logBlock("What's New reply", reply || "(empty)");
    if (!reply) throw new Error("Agent returned an empty response");

    const body = stripCodeFence(reply);

    // The refusal arrives as ordinary text over a *successful* stream, and can be
    // appended after the agent's own preamble, so a substring check is what
    // catches it. Throwing is what stops it being persisted as the day's summary.
    if (looksLikeRefusal(body)) {
      throw new Error(`Agent refused: ${body.slice(-200)}`);
    }

    // No markup means we didn't get a dashboard.
    if (!looksLikeMarkup(body)) {
      throw new Error(`Agent returned no markup: ${body.slice(0, 200)}`);
    }

    return {
      html: extractMarkup(body),
      // Match ids against the full reply: the agent often names machines in its
      // narration that it then charts, and those links are still wanted.
      machines: extractMachineRefs(body, input.park),
      role: input.role,
    };
  } finally {
    clearTimeout(timeout);
    // The briefing is a one-shot: leaving the session behind would add an entry
    // to the user's chat history every day. Best-effort only.
    void deleteSession(token, session.id).catch(() => {
      /* orphan session is cosmetic */
    });
  }
}
